import type { IMonitoringVisitRepository } from "../repositories/MonitoringVisitRepository";
import type {
  MonitoringVisit,
  CreateMonitoringVisitRequest,
  UpdateMonitoringVisitRequest,
  MonitoringVisitListResponse,
  PlanMonitoringVisitRequest,
  ScheduleMonitoringVisitRequest,
  ConfirmMonitoringVisitRequest,
  FinalizeMonitoringVisitRequest,
  MonitoringVisitFilters,
} from "../pages/MonitoringTracker/types/monitoringVisit.types";
import { getErrorMessage, isApiError } from "../utils/errorMessage";
export interface IMonitoringVisitUseCase {
  createVisit(visit: CreateMonitoringVisitRequest): Promise<MonitoringVisit>;
  getVisits(
    page?: number,
    limit?: number,
    filters?: MonitoringVisitFilters,
  ): Promise<MonitoringVisitListResponse>;
  getVisitById(id: string): Promise<MonitoringVisit>;
  updateVisit(
    id: string,
    updates: UpdateMonitoringVisitRequest,
  ): Promise<MonitoringVisit>;
  deleteVisit(id: string): Promise<void>;
  getVisitsByStudy(studyId: string): Promise<MonitoringVisit[]>;
  getVisitsBySite(siteId: string): Promise<MonitoringVisit[]>;
  planVisit(visit: PlanMonitoringVisitRequest): Promise<MonitoringVisit>;
  /** @deprecated Use planVisit instead */
  scheduleVisit(
    visit: ScheduleMonitoringVisitRequest,
  ): Promise<MonitoringVisit>;
  confirmVisit(
    id: string,
    data: ConfirmMonitoringVisitRequest,
  ): Promise<MonitoringVisit>;
  finalizeVisit(
    id: string,
    data: FinalizeMonitoringVisitRequest,
  ): Promise<MonitoringVisit>;
}
export class MonitoringVisitUseCase implements IMonitoringVisitUseCase {
  constructor(private monitoringVisitRepository: IMonitoringVisitRepository) {}
  private isValidVisitType(value: string): boolean {
    const validVisitTypes = ["Pre-Site", "Interim", "Close-Out"];
    return validVisitTypes.includes(value);
  }
  async createVisit(
    visit: CreateMonitoringVisitRequest,
  ): Promise<MonitoringVisit> {
    // Validate required fields
    if (!visit.visitDate?.trim()) {
      throw new Error("Visit date is required");
    }
    if (!visit.visitType) {
      throw new Error("Visit type is required");
    }
    if (!visit.studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!visit.siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    // Validate visit type
    if (!this.isValidVisitType(visit.visitType)) {
      throw new Error("Invalid visit type");
    }
    // Validate date format
    const visitDate = new Date(visit.visitDate);
    if (isNaN(visitDate.getTime())) {
      throw new Error("Invalid visit date format");
    }
    try {
      return await this.monitoringVisitRepository.createVisit(visit);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to create monitoring visit",
        );
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(
          getErrorMessage(error, "Invalid monitoring visit data provided"),
        );
      }
      throw new Error(
        getErrorMessage(error, "Failed to create monitoring visit"),
      );
    }
  }
  async getVisits(
    page = 1,
    limit = 10,
    filters?: MonitoringVisitFilters,
  ): Promise<MonitoringVisitListResponse> {
    // Validate pagination parameters
    if (page < 1) {
      throw new Error("Page number must be greater than 0");
    }
    if (limit < 1 || limit > 100) {
      throw new Error("Limit must be between 1 and 100");
    }
    return await this.monitoringVisitRepository.getVisits(page, limit, filters);
  }
  async getVisitById(id: string): Promise<MonitoringVisit> {
    if (!id?.trim()) {
      throw new Error("Monitoring visit ID is required");
    }
    return await this.monitoringVisitRepository.getVisitById(id);
  }
  async updateVisit(
    id: string,
    updates: UpdateMonitoringVisitRequest,
  ): Promise<MonitoringVisit> {
    if (!id?.trim()) {
      throw new Error("Monitoring visit ID is required");
    }
    // Validate visit type if provided
    if (updates.visitType) {
      if (!this.isValidVisitType(updates.visitType)) {
        throw new Error("Invalid visit type");
      }
    }
    // Validate date format if provided
    if (updates.visitDate) {
      const visitDate = new Date(updates.visitDate);
      if (isNaN(visitDate.getTime())) {
        throw new Error("Invalid visit date format");
      }
    }
    return await this.monitoringVisitRepository.updateVisit(id, updates);
  }
  async deleteVisit(id: string): Promise<void> {
    if (!id?.trim()) {
      throw new Error("Monitoring visit ID is required");
    }
    await this.monitoringVisitRepository.deleteVisit(id);
  }
  async getVisitsByStudy(studyId: string): Promise<MonitoringVisit[]> {
    if (!studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    return await this.monitoringVisitRepository.getVisitsByStudy(studyId);
  }
  async getVisitsBySite(siteId: string): Promise<MonitoringVisit[]> {
    if (!siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    return await this.monitoringVisitRepository.getVisitsBySite(siteId);
  }
  /**
   * Plan a new monitoring visit for a future date
   * @param visit - The visit planning data
   * @returns The planned monitoring visit
   */
  async planVisit(visit: PlanMonitoringVisitRequest): Promise<MonitoringVisit> {
    // Validate required fields
    if (!visit.studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!visit.siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    if (!visit.visitType) {
      throw new Error("Visit type is required");
    }
    if (!visit.plannedDate?.trim()) {
      throw new Error("Planned date is required");
    }
    // Validate visit type
    const validVisitTypes = ["Pre-Site", "Interim", "Close-Out"];
    if (!validVisitTypes.includes(visit.visitType)) {
      throw new Error("Invalid visit type");
    }
    // Validate date format
    const plannedDate = new Date(visit.plannedDate);
    if (isNaN(plannedDate.getTime())) {
      throw new Error("Invalid planned date format");
    }
    try {
      return await this.monitoringVisitRepository.planVisit(visit);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to plan monitoring visit",
        );
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(
          getErrorMessage(error, "Invalid monitoring visit data provided"),
        );
      }
      throw new Error(
        getErrorMessage(
          error,
          "Failed to plan monitoring visit. Please try again.",
        ),
      );
    }
  }
  /**
   * @deprecated Use planVisit instead
   */
  async scheduleVisit(
    visit: ScheduleMonitoringVisitRequest,
  ): Promise<MonitoringVisit> {
    // Validate required fields
    if (!visit.studyId?.trim()) {
      throw new Error("Study ID is required");
    }
    if (!visit.siteId?.trim()) {
      throw new Error("Site ID is required");
    }
    if (!visit.visitType) {
      throw new Error("Visit type is required");
    }
    if (!visit.scheduledDate?.trim()) {
      throw new Error("Scheduled date is required");
    }
    // Validate visit type
    const validVisitTypes = ["Pre-Site", "Interim", "Close-Out"];
    if (!validVisitTypes.includes(visit.visitType)) {
      throw new Error("Invalid visit type");
    }
    // Validate date format
    const scheduledDate = new Date(visit.scheduledDate);
    if (isNaN(scheduledDate.getTime())) {
      throw new Error("Invalid scheduled date format");
    }
    try {
      return await this.monitoringVisitRepository.scheduleVisit(visit);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: Insufficient permissions to schedule monitoring visit",
        );
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(
          getErrorMessage(error, "Invalid monitoring visit data provided"),
        );
      }
      throw new Error(
        getErrorMessage(error, "Failed to schedule monitoring visit"),
      );
    }
  }
  async confirmVisit(
    id: string,
    data: ConfirmMonitoringVisitRequest,
  ): Promise<MonitoringVisit> {
    if (!id?.trim()) {
      throw new Error("Monitoring visit ID is required");
    }
    if (!data.visitDate?.trim()) {
      throw new Error("Visit date is required for confirmation");
    }
    // Validate date format
    const visitDate = new Date(data.visitDate);
    if (isNaN(visitDate.getTime())) {
      throw new Error("Invalid visit date format");
    }
    try {
      return await this.monitoringVisitRepository.confirmVisit(id, data);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: You can only confirm visits you created",
        );
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(
          getErrorMessage(error, "Invalid confirmation data provided"),
        );
      }
      throw new Error(
        getErrorMessage(error, "Failed to confirm monitoring visit"),
      );
    }
  }
  async finalizeVisit(
    id: string,
    data: FinalizeMonitoringVisitRequest,
  ): Promise<MonitoringVisit> {
    if (!id?.trim()) {
      throw new Error("Monitoring visit ID is required");
    }
    if (!data.findingsSummary?.trim()) {
      throw new Error("Findings summary is required for finalization");
    }
    try {
      return await this.monitoringVisitRepository.finalizeVisit(id, data);
    } catch (error: unknown) {
      if (isApiError(error) && error.status === 401) {
        throw new Error("Authentication failed: Please login again");
      }
      if (isApiError(error) && error.status === 403) {
        throw new Error(
          "Access denied: You can only finalize visits you created",
        );
      }
      if (isApiError(error) && error.status === 400) {
        throw new Error(
          getErrorMessage(error, "Invalid finalization data provided"),
        );
      }
      throw new Error(
        getErrorMessage(error, "Failed to finalize monitoring visit"),
      );
    }
  }
}
